/**
 * ECS Video with Text Component
 * Handles video playback controls, play/pause functionality, and iframe video integration
 */
class EcsVideoWithText {
  constructor() {
    this.selectors = {
      playButton: '.ecs-video-with-text__play-button',
      videoContainer: '.ecs-video-with-text__video-container',
      overlay: '.ecs-video-with-text__video-overlay',
      posterImage: '.ecs-video-with-text__poster-image',
      section: '.ecs-video-with-text',
      triggerButton: '[data-trigger-video]'
    };

    this.init();
  }

  /**
   * Initialize event listeners
   */
  init() {
    document.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Main click event handler - routes to appropriate method based on target
   */
  handleClick(event) {
    const { target } = event;

    if (target.closest(this.selectors.playButton)) {
      this.handlePlayButtonClick(event);
    } else if (target.tagName === 'VIDEO') {
      this.handleVideoClick(event);
    } else if (target.closest(this.selectors.triggerButton)) {
      this.handleTriggerButtonClick(event);
    } else if (target.closest(this.selectors.videoContainer) &&
               !target.closest(this.selectors.playButton)) {
      this.handleVideoContainerClick(event);
    }
  }

  /**
   * Get video elements from container
   */
  getVideoElements(container) {
    if (!container) return {};

    return {
      video: container.querySelector('video'),
      iframe: container.querySelector('iframe'),
      playButton: container.querySelector(this.selectors.playButton),
      overlay: container.querySelector(this.selectors.overlay),
      posterImage: container.querySelector(this.selectors.posterImage)
    };
  }

  /**
   * Hide video controls and overlay
   */
  hideVideoControls(elements) {
    const { playButton, overlay, posterImage } = elements;

    if (playButton) {
      playButton.style.opacity = '0';
      playButton.style.pointerEvents = 'none';
    }

    if (overlay) {
      overlay.style.opacity = '0';
      overlay.style.pointerEvents = 'none';
    }

    if (posterImage) {
      posterImage.style.display = 'none';
    }
  }

  /**
   * Show video controls and overlay
   */
  showVideoControls(elements) {
    const { playButton, overlay, posterImage } = elements;

    if (playButton) {
      playButton.style.opacity = '1';
      playButton.style.pointerEvents = 'auto';
      playButton.style.display = 'flex';
    }

    if (overlay) {
      overlay.style.opacity = overlay.dataset.videoOverlayOpacity;
    }

    if (posterImage) {
      posterImage.style.display = 'block';
    }
  }

  /**
   * Play video based on type (native video or iframe)
   */
  async playVideo(elements, shouldScroll = false) {
    const { video, iframe, playButton, overlay, posterImage } = elements;

    this.hideVideoControls({ playButton, overlay, posterImage });

    if (video) {
      try {
        await video.play();
      } catch (error) {
        console.error('Video play failed:', error);
        this.showVideoControls({ playButton, overlay, posterImage });
        return;
      }
    } else if (iframe) {
      this.playIframeVideo(iframe);
    }

    if (shouldScroll) {
      const videoContainer = video?.closest(this.selectors.videoContainer) ||
                           iframe?.closest(this.selectors.videoContainer);
      this.scrollToVideo(videoContainer);
    }
  }

  /**
   * Handle iframe video autoplay (YouTube/Vimeo)
   */
  playIframeVideo(iframe) {
    if (!iframe?.src) return;

    const { src } = iframe;
    const separator = src.includes('?') ? '&' : '?';

    if (src.includes('youtube.com') || src.includes('vimeo.com')) {
      iframe.src = `${src}${separator}autoplay=1`;
    }
  }

  /**
   * Pause video and show controls
   */
  pauseVideo(video, elements) {
    if (!video) return;

    video.pause();
    this.showVideoControls(elements);
  }

  /**
   * Scroll video container into view
   */
  scrollToVideo(container) {
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Handle play button click
   */
  handlePlayButtonClick(event) {
    const playButton = event.target.closest(this.selectors.playButton);
    const videoContainer = playButton?.closest(this.selectors.videoContainer);

    if (!videoContainer) return;

    const elements = this.getVideoElements(videoContainer);
    this.playVideo(elements);
  }

  /**
   * Handle direct video click for play/pause toggle
   */
  handleVideoClick(event) {
    const video = event.target;
    const videoContainer = video.closest(this.selectors.videoContainer);

    if (!videoContainer) return;

    const elements = this.getVideoElements(videoContainer);

    if (video.paused) {
      this.playVideo(elements);
    } else {
      this.pauseVideo(video, elements);
    }
  }

  /**
   * Handle clicks anywhere in video container (except play button)
   */
  handleVideoContainerClick(event) {
    const videoContainer = event.target.closest(this.selectors.videoContainer);
    if (!videoContainer) return;

    const elements = this.getVideoElements(videoContainer);
    const { video } = elements;

    if (video && !video.paused) {
      this.pauseVideo(video, elements);
    }
  }

  /**
   * Handle trigger button clicks (external buttons that trigger video play)
   */
  handleTriggerButtonClick(event) {
    event.preventDefault();

    const section = event.target.closest(this.selectors.section);
    const videoContainer = section?.querySelector(this.selectors.videoContainer);

    if (!videoContainer) return;

    const elements = this.getVideoElements(videoContainer);
    this.playVideo(elements, true); // true = should scroll to video
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new EcsVideoWithText();
});
